import { SVGIconProps } from "@faire/slate-icons/types";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import {
  FilterButtonBase,
  filterButtonVars,
} from "@faire/slate/private/filter/FilterBase";
import { IFilterToggleWebProps } from "@faire/slate/private/filter/types";
import React from "react";

type IProps = {
  /**
   * Whether the filter is applied.
   *
   * @default false
   */
  applied?: boolean;

  /**
   * The background color of the filter when it is applied.
   */
  appliedBackgroundColor?: string;

  /**
   * The default text color of the filter.
   */
  defaultContentColor?: string;

  /**
   * The filter icon.
   */
  Icon?: React.FC<SVGIconProps>;

  /**
   * The filter label.
   */
  label: string;

  /**
   * Whether the filter is disabled.
   *
   * @default false
   */
  disabled?: boolean;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IFilterToggleWebProps;

/**
 * Filters components help users view a subset of content.
 *
 * Filter Toggle is intended to be used as a toggle that changes state when clicked.
 *
 * @remarks
 * Differently from Filter Button, it should change state when clicked, as a toggle should behave.
 * This is a very important distinction because it changes the way the component is implemented from
 * an accessibility standpoint.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Filter-2242efb5c25a80b8adb5c588806269bf | Slate/FilterToggle}
 */
export const FilterToggle: React.FC<IProps> = ({
  applied = false,
  appliedBackgroundColor,
  defaultContentColor,
  ...props
}) => {
  const { disabled } = props;
  const filterToggleCls = filterToggleStyle.cls({
    applied,
    className: componentCls(),
  });

  let styleOverrides: React.CSSProperties = {};

  if (!disabled) {
    if (!applied && defaultContentColor) {
      styleOverrides = filterButtonVars({
        textColor: defaultContentColor,
        iconColor: defaultContentColor,
      });
    }

    if (applied && appliedBackgroundColor) {
      styleOverrides = {
        ...styleOverrides,
        ...filterButtonVars({
          backgroundColor: appliedBackgroundColor,
          borderColor: appliedBackgroundColor,
        }),
      };
    }
  }

  return (
    <FilterButtonBase
      {...props}
      aria-checked={applied}
      className={filterToggleCls}
      role="switch"
      style={styleOverrides}
      data-component-name="Slate/FilterToggle"
    />
  );
};

const filterToggleStyle = tsv({
  variants: {
    applied: {
      true: "fs-filter--applied",
    },
  },
});
